import type Phaser from "phaser";
import { fallbackLook, newWireLook, settleWireLook, type LookLayout, type LookManifest, type Looks, type WireLook } from "odyssey-shared";
import { FALLBACK_LOOK, LookBlocks } from "./look-blocks.ts";
import { LOOK_CACHE, lookBlockCount, type LookCacheKnobs } from "./look-cache-knobs.ts";
import { LookPages } from "./look-pages.ts";
import { composeSheets, lookKey, lookStrips, newComposePlan, type LookStrips } from "./look-plan.ts";
import { LookSheets, sheetTextureKey, type SheetLoader } from "./look-sheets.ts";

/**
 * The look cache (ADR-094 decisions 14 to 17): every composed character on a page draws from it.
 *
 * It names looks (`retain` and `release`, a hold a character), loads the piece sheets a look needs as GPU textures
 * (`look-sheets.ts`), decides which look holds which block (`look-blocks.ts`) and draws a look into its block
 * (`look-pages.ts`). Each frame the scene calls `settle` with where the page's own character stands, which composes
 * at most `LOOK_CACHE.composesPerFrame` blocks, nearest first; each drawn character then asks `block` for the block
 * to show, its look's or the fallback's; and the scene pins the own character's look and the target's (`pin`).
 *
 * **One a page.** The pages' and the sheets' texture keys are the game's, so a game has one look cache, which
 * players and the developer's crowd share; making a second throws.
 *
 * **Bounds.** At most `LOOK_CACHE.pages` look pages (5 of 2048 square, 16 MiB each, made as their first block is
 * needed and kept): 120 blocks, the fallback's and 119 looks. Past that the looks farthest from the own character wear
 * the fallback (`look-blocks.ts` says who gives up a block). At most `LOOK_CACHE.looks` looks are named at once, a
 * block or not. The piece sheets are at most the pointer's (64 for the first table, 0.64 MiB each) and none is let go.
 *
 * **A frame allocates nothing once warm:** `settle`, `block` and `pin` read typed arrays; a compose, which happens
 * only while looks are arriving, queues its draws in Phaser's command buffer; a look's key and its plan are made when
 * it is first named.
 *
 * After a lost WebGL context the pages are empty: every block is composed again from the looks the cache still
 * names, the fallback first, at the same few a frame. While the context is lost nothing is composed, since a render
 * target can be neither made nor drawn into; the frame's claims wait for the first `settle` after it is back.
 */

/**
 * The renderer's event for a WebGL context that came back (`Phaser.Renderer.Events.RESTORE_WEBGL`), named here so the
 * cache imports no Phaser code and runs in a unit test.
 */
const RESTORE_WEBGL = "restorewebgl";

/**
 * What the cache reads off the game: its textures, and its renderer's restore event and whether its WebGL context is
 * lost now.
 */
export interface LookCacheHost {
  readonly textures: Phaser.Textures.TextureManager;
  readonly renderer: { readonly contextLost?: boolean; on(event: string, listener: () => void): unknown; off(event: string, listener: () => void): unknown };
}

/**
 * What the cache holds now, for tests, rigs and the row's measurements.
 */
export interface LookCacheStats {
  /** Look pages made. */
  readonly pages: number;
  /** Blocks holding a look, the fallback's included. */
  readonly blocks: number;
  /** Looks named, the fallback included. */
  readonly looks: number;
  /** Piece sheets that are textures. */
  readonly sheets: number;
  /** Piece sheets being fetched, and waiting to be. */
  readonly fetching: number;
  readonly waiting: number;
  /** Composes drawn since the cache was made, and the most drawn in one frame. */
  readonly composes: number;
  readonly mostInFrame: number;
  /** Whether the fallback look is drawn, without which nothing composed shows. */
  readonly ready: boolean;
}

/**
 * The look cache of one game.
 */
export class LookCache {
  /** The look layout the page's pointer carries. */
  public readonly layout: LookLayout;
  /** The strips every composed character plays. */
  public readonly strips: LookStrips;
  private readonly blocks: LookBlocks;
  private readonly pages: LookPages;
  private readonly sheets: LookSheets;
  private readonly plans: (readonly string[] | null)[];
  private readonly sheetIds: (readonly string[] | null)[];
  private readonly pending = new Set<number>();
  private readonly settled = newWireLook();
  private readonly plan = newComposePlan();
  private focusX = 0;
  private focusY = 0;
  private composes = 0;
  private mostInFrame = 0;
  private warnedFallback = false;
  private fallbackWaits: ((loaded: boolean) => void)[] = [];
  private fallbackFailed = false;

  /** The fallback look (decision 15), which a character wears until it is told another and when it loses one. */
  public readonly fallback: Readonly<WireLook>;

  /**
   * Invalidates every block when the WebGL context comes back, since the pages came back empty.
   */
  private readonly onRestore = (): void => {
    this.blocks.invalidate();
  };

  /**
   * @param host The game's textures and renderer.
   * @param base The asset base the pointer's paths are relative to.
   * @param looks The page's looks table.
   * @param manifest The look sheets' pointer, validated.
   * @param knobs The cache's knobs; `LOOK_CACHE` unless a test hands in others.
   * @param load What loads a sheet's image; the page's unless a test hands in another.
   * @throws Error when the game has a look cache already: a page has one, shared by everything that draws composed
   *   characters, since the pages' and the sheets' texture keys are the game's.
   * @throws RangeError when the pages hold fewer than two blocks of the layout's size.
   */
  public constructor(
    private readonly host: LookCacheHost,
    base: string,
    private readonly looks: Looks,
    public readonly manifest: LookManifest,
    knobs: LookCacheKnobs = LOOK_CACHE,
    load?: SheetLoader,
  ) {
    this.layout = manifest.layout;
    this.strips = lookStrips(manifest.layout);
    const fallback = fallbackLook(looks, newWireLook());
    this.fallback = fallback;
    this.pages = new LookPages(host.textures, manifest.layout, knobs.pageSize, knobs.pages);
    this.blocks = new LookBlocks({ blocks: lookBlockCount(manifest.layout.block, knobs), looks: knobs.looks, composesPerFrame: knobs.composesPerFrame, evictMargin: knobs.evictMargin }, lookKey(fallback));
    this.sheets = new LookSheets(
      host.textures,
      base,
      manifest.sheets,
      knobs,
      () => this.sheetLoaded(),
      (sheet) => this.sheetNeeded(sheet),
      load,
    );
    this.sheets.onFailed = (sheet) => this.sheetFailed(sheet);
    this.plans = new Array<readonly string[] | null>(knobs.looks).fill(null);
    this.sheetIds = new Array<readonly string[] | null>(knobs.looks).fill(null);
    this.pages.ensure(0);
    this.planLook(FALLBACK_LOOK, fallback);
    host.renderer.on(RESTORE_WEBGL, this.onRestore);
  }

  /**
   * Whether the fallback look is drawn. Until it is, a composed character has nothing to show.
   *
   * @returns True once block 0 is composed.
   */
  public get ready(): boolean {
    return this.blocks.composed(FALLBACK_LOOK);
  }

  /**
   * How many piece sheets the fallback look is composed from: what a page loads before it joins.
   *
   * @returns The count.
   */
  public get fallbackSheets(): number {
    return this.sheetIds[FALLBACK_LOOK]?.length ?? 0;
  }

  /**
   * How many of the fallback look's sheets are textures now.
   *
   * @returns The count, at most `fallbackSheets`.
   */
  public get fallbackSheetsLoaded(): number {
    let loaded = 0;
    for (const sheet of this.sheetIds[FALLBACK_LOOK] ?? []) {
      if (this.sheets.has(sheet)) {
        loaded += 1;
      }
    }
    return loaded;
  }

  /**
   * Waits for the fallback look's sheets, which a page loads before it joins (decision 16).
   *
   * @returns A promise of true once every one is a texture, or of false as soon as one of them fails to load; it
   *   never rejects.
   */
  public fallbackLoads(): Promise<boolean> {
    if (this.blocks.ready(FALLBACK_LOOK)) {
      return Promise.resolve(true);
    }
    if (this.fallbackFailed) {
      return Promise.resolve(false);
    }
    return new Promise((resolve) => {
      this.fallbackWaits.push(resolve);
    });
  }

  /**
   * Answers everyone waiting on the fallback look's sheets.
   *
   * @param loaded Whether they all loaded.
   */
  private answerFallback(loaded: boolean): void {
    const waits = this.fallbackWaits;
    if (waits.length === 0) {
      return;
    }
    this.fallbackWaits = [];
    for (const wait of waits) {
      wait(loaded);
    }
  }

  /**
   * A sheet's fetch failed: when the fallback look is composed from it, whoever waits on the fallback is told.
   *
   * @param sheet The sheet's id.
   */
  private sheetFailed(sheet: string): void {
    if (this.sheetIds[FALLBACK_LOOK]?.includes(sheet) === true && !this.blocks.ready(FALLBACK_LOOK)) {
      this.fallbackFailed = true;
      this.answerFallback(false);
    }
  }

  /**
   * Whether a look's block shows it now, so a character wearing it is drawn as itself.
   *
   * @param id The look's entry.
   * @returns True for the fallback look once it is drawn, and for another look once its block is composed.
   */
  public composed(id: number): boolean {
    return this.blocks.composed(id);
  }

  /**
   * What the cache holds now.
   *
   * @returns The readings.
   */
  public get stats(): LookCacheStats {
    return { pages: this.pages.made, blocks: this.blocks.used, looks: this.blocks.live, sheets: this.sheets.loaded, fetching: this.sheets.inFlight, waiting: this.sheets.waiting, composes: this.composes, mostInFrame: this.mostInFrame, ready: this.ready };
  }

  /**
   * Takes a hold on a look for one character, as it comes into view; a look the cache did not name has its sheets
   * asked for. Not a frame's work: it builds the look's key.
   *
   * @param look The look as it arrived; it is settled against the page's table here.
   * @returns The look's entry, for `block`, `pin` and `release`.
   */
  public retain(look: Readonly<WireLook>): number {
    settleWireLook(this.looks, look, this.settled);
    const id = this.blocks.retain(lookKey(this.settled));
    if (this.blocks.created) {
      this.planLook(id, this.settled);
    }
    return id;
  }

  /**
   * Lets go of one character's hold on a look, as it leaves the view.
   *
   * @param id The look's entry, from `retain`.
   */
  public release(id: number): void {
    this.blocks.release(id);
  }

  /**
   * Works out the sheets a newly named look is composed from, asks for them, and marks the look ready if they are
   * all loaded already.
   *
   * @param id The look's entry.
   * @param look The look, settled.
   */
  private planLook(id: number, look: Readonly<WireLook>): void {
    composeSheets(this.looks, this.layout.compose, this.manifest.sheets, look, this.plan);
    if (id === FALLBACK_LOOK && this.plan.fellBack > 0 && !this.warnedFallback) {
      this.warnedFallback = true;
      console.warn(`the fallback look is short of ${this.plan.fellBack} of its pieces: the look sheets' pointer does not name them`);
    }
    const ids = [...this.plan.sheets];
    this.sheetIds[id] = ids;
    this.plans[id] = ids.map((sheet) => sheetTextureKey(sheet));
    this.pending.add(id);
    for (const sheet of ids) {
      this.sheets.request(sheet);
    }
    this.sheetLoaded();
  }

  /**
   * Marks ready every look that waited for sheets and now has them all, and forgets the waits of looks the cache no
   * longer names. Runs as a sheet loads and as a look is named, never in a frame that does neither.
   */
  private sheetLoaded(): void {
    for (const id of this.pending) {
      const ids = this.sheetIds[id];
      if (ids === null || ids === undefined || !this.blocks.named(id)) {
        this.pending.delete(id);
        continue;
      }
      if (ids.every((sheet) => this.sheets.has(sheet))) {
        this.blocks.setReady(id, true);
        this.pending.delete(id);
        if (id === FALLBACK_LOOK) {
          this.fallbackFailed = false;
          this.answerFallback(true);
        }
      }
    }
  }

  /**
   * Whether a look the cache names still waits for a sheet, which is what keeps a failed sheet being asked for.
   *
   * @param sheet The sheet's id.
   * @returns True while a named look that is not ready is composed from it.
   */
  private sheetNeeded(sheet: string): boolean {
    for (const id of this.pending) {
      if (this.blocks.named(id) && this.sheetIds[id]?.includes(sheet) === true) {
        return true;
      }
    }
    return false;
  }

  /**
   * Closes the frame before and composes what it planned: at most the knob's blocks, nearest first. Called once a
   * frame, before any character asks for its block.
   *
   * @param focusX Where the page's own character stands, in world pixels, which this frame's distances are from.
   * @param focusY The same, down.
   * @param nowMs The time in ms, against which a failed sheet waits.
   * @returns How many blocks were composed; none while the WebGL context is lost.
   */
  public settle(focusX: number, focusY: number, nowMs: number): number {
    this.focusX = focusX;
    this.focusY = focusY;
    this.sheets.retry(nowMs);
    if (this.host.renderer.contextLost === true) {
      return 0;
    }
    const jobs = this.blocks.settle();
    for (let job = 0; job < jobs; job += 1) {
      this.pages.compose(this.blocks.jobBlocks[job] as number, this.plans[this.blocks.jobLooks[job] as number] as readonly string[]);
    }
    if (jobs > 0) {
      this.composes += jobs;
      if (jobs > this.mostInFrame) {
        this.mostInFrame = jobs;
      }
    }
    return jobs;
  }

  /**
   * The block a drawn character shows this frame, and its look's claim to one: the nearer the own character, the
   * stronger.
   *
   * @param id The look's entry.
   * @param x Where the character stands, in world pixels.
   * @param y The same, down.
   * @returns The block: the look's once composed, the fallback's until then.
   */
  public block(id: number, x: number, y: number): number {
    const dx = x - this.focusX;
    const dy = y - this.focusY;
    return this.blocks.want(id, Math.sqrt(dx * dx + dy * dy));
  }

  /**
   * Keeps a look's block for this frame whatever its distance: the own character's and the target's, said each frame.
   *
   * @param id The look's entry.
   */
  public pin(id: number): void {
    this.blocks.pin(id);
  }

  /**
   * The key of the page a block lies on.
   *
   * @param block The block's number.
   * @returns The page's texture key.
   */
  public pageKey(block: number): string {
    return this.pages.keys[this.pages.pageOf(block)] as string;
  }

  /**
   * The name of the frame showing a cell of a block.
   *
   * @param block The block's number.
   * @param cell The cell, its place in the layout's `cells`.
   * @returns The frame's name on the block's page.
   */
  public frame(block: number, cell: number): number {
    return this.pages.frameOf(block, cell);
  }

  /**
   * Composes one block now from sheets already loaded, outside the frame's plan: what a rig times.
   *
   * @param block The block's number.
   * @param id The entry of the look to draw into it, whose sheets are loaded.
   */
  public composeNow(block: number, id: number): void {
    this.pages.compose(block, this.plans[id] as readonly string[]);
  }

  /**
   * The texture keys of the sheets a look is composed from, bottom first.
   *
   * @param id The look's entry.
   * @returns The keys, or none for an entry the cache does not name.
   */
  public sheetsOf(id: number): readonly string[] {
    return this.plans[id] ?? [];
  }

  /**
   * The block a look holds, composed or not.
   *
   * @param id The look's entry.
   * @returns The block, or -1.
   */
  public blockOf(id: number): number {
    return this.blocks.blockOf(id);
  }

  /**
   * Removes the pages and the sheets and stops listening to the renderer.
   */
  public destroy(): void {
    this.host.renderer.off(RESTORE_WEBGL, this.onRestore);
    this.answerFallback(false);
    this.sheets.destroy();
    this.pages.destroy();
  }
}
